import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { currentRecentsKey } from '../../lib/api/recents-key.js';
import { isAppInFront, onAppForeground } from '../../lib/app-presence.js';
import { ensureFreshPlatformJwt } from '../../lib/sso.js';
import * as api from '../services/integration-api';
import { awaitTeamsUserId } from '../services/teams.js';
import { useChatSessionStore } from '../state/chat-session-store.js';
import type { Conversation } from '../types';

import { broadcastRead, broadcastUnread } from './broadcast-channel.js';
import { dispatchServerEvent, type ServerEvent } from './server-events.js';
import { recoverFrom401 } from './stream-auth.js';

type ConversationActivityEvent = Extract<
  ServerEvent,
  { type: 'conversation.activity' }
>;
type ConversationLifecycleEvent = Extract<
  ServerEvent,
  { type: 'conversation.archived' | 'conversation.restored' }
>;

const KNOWN_EVENT_TYPES = new Set<ServerEvent['type']>([
  'conversation.activity',
  'conversation.archived',
  'conversation.restored',
  'prefs.changed',
  'connector.changed',
  'maintenance.changed',
  'access-request.changed',
  'release-note.published',
  'skill.suggestion',
  'memory.proposal',
]);

export function isServerEvent(value: unknown): value is ServerEvent {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  const t = record['type'];
  if (typeof t !== 'string') return false;
  if (!KNOWN_EVENT_TYPES.has(t as ServerEvent['type'])) return false;
  if (t === 'conversation.archived') {
    return (
      record['version'] === 1 &&
      typeof record['conversationId'] === 'string' &&
      typeof record['archivedAt'] === 'string'
    );
  }
  if (t === 'conversation.restored') {
    return (
      record['version'] === 1 &&
      typeof record['conversationId'] === 'string' &&
      record['archivedAt'] === null
    );
  }
  return true;
}

type RecentsCache = { pages: Conversation[][]; pageParams: number[] };

const RECONNECT_BACKOFFS_MS = [1_000, 2_000, 5_000, 10_000];

function patchRecents(qc: QueryClient, id: string, unread: boolean): void {
  qc.setQueryData<RecentsCache>(currentRecentsKey(), (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((p) =>
        p.map((c) => (c.id === id ? { ...c, unread } : c)),
      ),
    };
  });
}

function removeFromFamily(
  qc: QueryClient,
  family: 'recents' | 'archived-conversations',
  id: string,
): void {
  qc.setQueriesData<RecentsCache>({ queryKey: [family] }, (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page) => page.filter((row) => row.id !== id)),
    };
  });
}

/** Reconcile authoritative lifecycle/activity state without ever guessing archived rows into Recents. */
export function reconcileConversationEvent(
  qc: QueryClient,
  event: ConversationActivityEvent | ConversationLifecycleEvent,
  unread: boolean,
): void {
  const id = event.conversationId;
  const isLifecycle = event.type !== 'conversation.activity';
  const status =
    event.type === 'conversation.archived'
      ? 'archived'
      : event.type === 'conversation.restored'
        ? 'active'
        : event.status;
  const archivedAt = event.archivedAt;

  const invalidateAuthoritativeState = (): void => {
    void qc.invalidateQueries({ queryKey: ['recents'] });
    void qc.invalidateQueries({ queryKey: ['archived-conversations'] });
    void qc.invalidateQueries({ queryKey: ['conversation', id] });
    if (isLifecycle) {
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    }
  };

  if (
    status === undefined ||
    (status === 'archived' && typeof archivedAt !== 'string')
  ) {
    invalidateAuthoritativeState();
    return;
  }

  removeFromFamily(
    qc,
    status === 'archived' ? 'recents' : 'archived-conversations',
    id,
  );
  qc.setQueriesData<RecentsCache>(
    {
      queryKey: [status === 'archived' ? 'archived-conversations' : 'recents'],
    },
    (old) => {
      if (!old) return old;
      return {
        ...old,
        pages: old.pages.map((page) =>
          page.map((row) =>
            row.id === id
              ? { ...row, status, archivedAt: archivedAt ?? null, unread }
              : row,
          ),
        ),
      };
    },
  );
  qc.setQueryData<Conversation>(['conversation', id], (old) =>
    old ? { ...old, status, archivedAt: archivedAt ?? null, unread } : old,
  );
  invalidateAuthoritativeState();
}

async function readSseUserEvents(
  response: Response,
  onConversationActivity: (e: ConversationActivityEvent) => void,
  onConversationLifecycle: (e: ConversationLifecycleEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed.startsWith('data: ')) continue;
      try {
        const parsed: unknown = JSON.parse(trimmed.slice(6));
        if (!isServerEvent(parsed)) continue;
        // Every known event goes through the generic bus so components
        // can subscribe declaratively via `useServerEvent`.
        dispatchServerEvent(parsed);
        // Backwards-compat fast-path for the conversation-activity flow,
        // which has bespoke cache-mutation semantics (mark-read race with
        // the active chat) that don't fit a generic invalidate.
        if (
          parsed.type === 'conversation.activity' &&
          typeof parsed.conversationId === 'string'
        ) {
          onConversationActivity(parsed);
        } else if (
          (parsed.type === 'conversation.archived' ||
            parsed.type === 'conversation.restored') &&
          typeof parsed.conversationId === 'string' &&
          parsed.version === 1
        ) {
          onConversationLifecycle(parsed);
        }
      } catch {
        /* malformed event, skip */
      }
    }
  }
}

/**
 * Zero-trust: identity comes from the verified platform JWT. The SSE channel
 * uses fetch+ReadableStream (not native EventSource) so a custom Authorization
 * header is supported. The token is passed in, not read here, because the loop
 * needs the same freshly renewed value it decided to connect with.
 */
function buildHeaders(jwt: string): HeadersInit {
  return { Authorization: `Bearer ${jwt}` };
}

/**
 * Top-level subscriber to /v1/me/events. When the server reports a
 * background stream completion, paint the corresponding chat unread in
 * the sidebar cache — unless the user is currently looking at that
 * chat AND the app is in front of them (visible, and not a tab Teams keeps
 * suspended), in which case mark it read instead.
 *
 * Two mistakes from 06484f7 are explicitly avoided:
 * 1. The fetch awaits `awaitTeamsUserId()` before opening — first connect
 *    in the prior version raced with the Teams handshake, returned 401,
 *    and chewed CPU in the backoff loop.
 * 2. Cache writes go through a colocated `patchRecents` (inline
 *    setQueryData) — NOT through useMarkReadMutation, whose `onSettled`
 *    invalidate raced with paints and reverted dots to read.
 *
 * When the app comes back (a visible page again, or Teams resuming a
 * suspended tab) the stream reconnects at once instead of waiting in the
 * backoff queue: an iframe that sat hidden for minutes often holds a dead
 * SSE connection.
 */
export function useUserEventStream(enabled = true): void {
  const qc = useQueryClient();

  useEffect(() => {
    // Inert on the public share viewer: that path stays off the Teams SDK
    // (`awaitTeamsUserId` warms it) and off the authenticated event stream.
    // Hook order stays stable — the effect just does nothing when disabled.
    if (!enabled) return;
    const ctrl = new AbortController();
    let attempt = 0;
    let innerCtrl: AbortController | null = null;

    async function connectLoop(): Promise<void> {
      // Let the Teams handshake settle first (it warms the cached Teams
      // identity other surfaces read), but do NOT require a Teams user id —
      // in a standalone browser there is none and the stream must still
      // connect. Auth is the platform JWT, which the loop below waits for.
      await awaitTeamsUserId();
      if (ctrl.signal.aborted) return;

      while (!ctrl.signal.aborted) {
        // No JWT yet (standalone browser before sign-in) — wait instead of
        // burning a guaranteed 401 that would kill the loop. Renewing here,
        // once per attempt, is also what keeps a reconnect after hours of
        // streaming from reusing a token that died 15 minutes in.
        const jwt = await ensureFreshPlatformJwt();
        if (jwt === null || jwt.length === 0) {
          await new Promise((r) => setTimeout(r, 1_000));
          continue;
        }
        innerCtrl = new AbortController();
        const combined = new AbortController();
        const onOuterAbort = (): void => combined.abort();
        ctrl.signal.addEventListener('abort', onOuterAbort);
        innerCtrl.signal.addEventListener('abort', onOuterAbort);
        try {
          const res = await fetch('/v1/me/events', {
            headers: buildHeaders(jwt),
            signal: combined.signal,
          });
          if (res.status === 401) {
            if (!(await recoverFrom401(jwt))) return;
          } else {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            attempt = 0;
            await readSseUserEvents(
              res,
              (event) => {
                const active =
                  useChatSessionStore.getState().activeConversationId;
                if (event.conversationId === active && isAppInFront()) {
                  void api.markConversationRead(event.conversationId);
                  patchRecents(qc, event.conversationId, false);
                  broadcastRead(event.conversationId);
                  reconcileConversationEvent(qc, event, false);
                } else {
                  if (event.status === undefined) {
                    patchRecents(qc, event.conversationId, true);
                  }
                  reconcileConversationEvent(qc, event, true);
                  broadcastUnread(event.conversationId);
                }
              },
              (event) => {
                reconcileConversationEvent(qc, event, false);
              },
              combined.signal,
            );
          }
        } catch (err) {
          if (ctrl.signal.aborted) return;
          // eslint-disable-next-line no-console
          console.warn('[user-events] disconnected, will reconnect:', err);
        } finally {
          ctrl.signal.removeEventListener('abort', onOuterAbort);
        }
        if (ctrl.signal.aborted) return;
        const delay =
          RECONNECT_BACKOFFS_MS[
            Math.min(attempt, RECONNECT_BACKOFFS_MS.length - 1)
          ];
        attempt += 1;
        await new Promise((r) => setTimeout(r, delay));
      }
    }

    const stopForegroundReconnect = onAppForeground(() => {
      attempt = 0;
      innerCtrl?.abort();
    });

    void connectLoop();
    return () => {
      stopForegroundReconnect();
      ctrl.abort();
      innerCtrl?.abort();
    };
  }, [qc, enabled]);
}
